import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useStudyGroups, useJoinStudyGroup, useNotifications, useMarkNotificationRead, useMe, authedFetch, type StudyGroupListItem } from '../lib/api';
import { canScanQr, scanQrCode, roomCodeFromScan } from '../lib/qr';

export function StudyGroupsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useStudyGroups();
  const me = useMe();
  const isPro = me.data?.user.proAccess ?? false;
  const joinMutation = useJoinStudyGroup();
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const groups = data?.groups ?? [];
  const activeGroups = groups.filter((g) => ['lobby', 'countdown', 'active'].includes(g.status));
  const completedGroups = groups.filter((g) => g.status === 'completed');

  // Check for an in-progress session (countdown or active)
  const liveSession = activeGroups.find(
    (g) => g.status === 'countdown' || g.status === 'active',
  );

  const handleJoin = async () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length < 4) return;
    setJoinError('');
    try {
      const result = await joinMutation.mutateAsync(code);
      setJoinCode('');
      navigate(`/study-groups/${result.group.id}/lobby`);
    } catch (err: any) {
      setJoinError(err.message || 'Could not join. Check the code and try again.');
    }
  };

  const handleScan = async () => {
    setJoinError('');
    const value = await scanQrCode();
    if (!value) return;
    const code = roomCodeFromScan(value);
    if (!code) { setJoinError('That QR code is not a study group invite.'); return; }
    try {
      const result = await joinMutation.mutateAsync(code);
      navigate(`/study-groups/${result.group.id}/lobby`);
    } catch (err: any) {
      setJoinError(err.message || 'Could not join from that code.');
    }
  };

  const handleCancel = async (groupId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Cancel this study group?')) return;
    try {
      await authedFetch(`/study-groups/${groupId}`, { method: 'DELETE' });
      qc.invalidateQueries({ queryKey: ['study-groups'] });
    } catch { /* ignore */ }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-slate-500">
        Loading...
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto pb-24 space-y-5 px-4">
      <h1 className="text-xl font-bold text-squishy-dark">Study Groups</h1>

      {/* ── Pending invites ───────────────────────────────────── */}
      <InvitesSection />

      {/* ── Active session banner ──────────────────────────────── */}
      {liveSession && (
        <button
          onClick={() => {
            const dest = liveSession.status === 'active'
              ? `/study-groups/${liveSession.id}/play`
              : `/study-groups/${liveSession.id}/lobby`;
            navigate(dest);
          }}
          className="w-full bg-gradient-to-r from-squishy-primary to-teal-500 text-white rounded-xl p-4 text-left shadow-md hover:shadow-lg transition-shadow"
        >
          <div className="text-xs font-semibold uppercase tracking-wide opacity-80">
            Live Session
          </div>
          <div className="text-lg font-bold mt-1">Rejoin Active Session</div>
          <div className="text-sm opacity-90 mt-0.5">
            {liveSession.title} · {liveSession.questionCount} questions
          </div>
        </button>
      )}

      {/* ── Pro gate ──────────────────────────────────────────── */}
      {!isPro && (
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-5 text-center space-y-2">
          <div className="text-2xl">🎯</div>
          <h2 className="text-sm font-bold text-amber-900">Study Groups are a Pro feature</h2>
          <p className="text-xs text-amber-800 max-w-xs mx-auto">
            Go head to head with friends in live, timed quiz battles. Upgrade to Squishy Pro to create and join study groups.
          </p>
          <button
            onClick={() => navigate('/profile')}
            className="mt-1 inline-block bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-5 py-2 rounded-xl transition-colors"
          >
            Upgrade to Pro
          </button>
        </div>
      )}

      {/* ── Create button ─────────────────────────────────────── */}
      {isPro && (
        <button
          onClick={() => navigate('/study-groups/create')}
          className="w-full bg-squishy-primary hover:bg-teal-600 text-white font-semibold py-3 rounded-xl text-center transition-colors shadow-sm"
        >
          Create Study Group
        </button>
      )}

      {/* ── Join with code ────────────────────────────────────── */}
      {isPro && (
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <h2 className="font-medium text-squishy-dark text-sm">Join with Code</h2>
        <form
          onSubmit={(e) => { e.preventDefault(); handleJoin(); }}
          className="flex gap-2"
        >
          <input
            value={joinCode}
            onChange={(e) => { setJoinCode(e.target.value.toUpperCase()); setJoinError(''); }}
            placeholder="Enter room code"
            maxLength={8}
            className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono tracking-widest text-center uppercase focus:outline-none focus:ring-2 focus:ring-squishy-primary/40"
          />
          <button
            type="submit"
            disabled={joinMutation.isPending || joinCode.trim().length < 4}
            className="bg-squishy-primary text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-opacity"
          >
            Join
          </button>
        </form>
        {joinError && (
          <p className="text-xs text-red-500">{joinError}</p>
        )}
        {canScanQr() && (
          <button
            onClick={handleScan}
            className="w-full flex items-center justify-center gap-2 text-sm font-medium text-squishy-primary border border-squishy-primary/30 rounded-lg py-2 active:scale-[0.98] transition"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4v-4m-4 4l-4-4M4 4h4v4H4V4zm12 0h4v4h-4V4zM4 16h4v4H4v-4z" />
            </svg>
            Scan a group QR code
          </button>
        )}
      </div>
      )}

      {/* ── Active lobbies ────────────────────────────────────── */}
      {activeGroups.filter((g) => g.status === 'lobby').length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-squishy-dark">Active Lobbies</h2>
          {activeGroups
            .filter((g) => g.status === 'lobby')
            .map((g) => (
              <div
                key={g.id}
                onClick={() => navigate(`/study-groups/${g.id}/lobby`)}
                className="w-full bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-squishy-primary transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-squishy-dark">
                      {g.title}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {g.questionCount} questions · {g.memberCount}/{g.memberCap} players · {formatShortDate(g.createdAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2 shrink-0">
                    <div className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-1 rounded">
                      {g.roomCode}
                    </div>
                    <button
                      onClick={(e) => handleCancel(g.id, e)}
                      className="text-slate-400 hover:text-red-500 transition-colors p-1"
                      title="Cancel group"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </section>
      )}

      {/* ── Recent completed ──────────────────────────────────── */}
      {completedGroups.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-squishy-dark">Recent Sessions</h2>
          {completedGroups.map((g) => (
            <button
              key={g.id}
              onClick={() => navigate(`/study-groups/${g.id}/results`)}
              className="w-full bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-slate-300 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-squishy-dark">
                    {g.title}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {g.questionCount} questions · {g.memberCount} players
                  </div>
                </div>
                <div className="text-xs text-slate-400">
                  {formatRelativeTime(g.completedAt)}
                </div>
              </div>
            </button>
          ))}
        </section>
      )}

      {/* ── Empty state ───────────────────────────────────────── */}
      {activeGroups.length === 0 && completedGroups.length === 0 && (
        <div className="text-center py-12">
          <div className="text-4xl mb-3">📚</div>
          <h3 className="text-sm font-semibold text-squishy-dark">No study groups yet</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">
            Create a group and invite friends to quiz each other in real time.
          </p>
        </div>
      )}
    </div>
  );
}

// Pending study-group invites (from notifications). Tapping Join enters the
// lobby via the invite's room code and clears the notification.
function InvitesSection() {
  const navigate = useNavigate();
  const { data } = useNotifications(15);
  const joinMutation = useJoinStudyGroup();
  const markRead = useMarkNotificationRead();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errId, setErrId] = useState<string | null>(null);

  const invites = (data?.notifications ?? []).filter(
    (n: any) => n.type === 'study_group_invite' && !n.read && n.data?.roomCode,
  );
  if (invites.length === 0) return null;

  const accept = async (n: any) => {
    setBusyId(n.id);
    setErrId(null);
    try {
      const result = await joinMutation.mutateAsync(String(n.data.roomCode).toUpperCase());
      markRead.mutate(n.id);
      navigate(`/study-groups/${result.group.id}/lobby`);
    } catch {
      setErrId(n.id);
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-2">
      <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Invites</h2>
      {invites.map((n: any) => (
        <div key={n.id} className="bg-white border-2 border-squishy-primary/30 rounded-xl p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center shrink-0 text-lg">&#x1F3AE;</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-squishy-dark truncate">{n.body || 'Study group invite'}</div>
            {errId === n.id
              ? <div className="text-[11px] text-red-500 mt-0.5">That group is no longer available.</div>
              : <div className="text-[11px] text-slate-400 mt-0.5">Tap Join to enter the lobby.</div>}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => markRead.mutate(n.id)}
              className="text-[11px] text-slate-400 hover:text-slate-600 px-2 py-1"
            >
              Dismiss
            </button>
            <button
              onClick={() => accept(n)}
              disabled={busyId === n.id}
              className="bg-squishy-primary text-white text-xs font-semibold px-4 py-2 rounded-lg active:scale-95 transition disabled:opacity-50"
            >
              {busyId === n.id ? '...' : 'Join'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3_600_000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
